import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export interface User {
  id: string
  email: string
  name: string
  avatar?: string

  // Plan configuration (set by admin)
  plan: 'free' | 'pro' | 'business' | 'enterprise' | 'custom'
  planName?: string // Custom plan name (e.g., "Piano Alessandro")
  planPrice?: number // Monthly price in EUR
  planStartDate?: string // Plan activation date

  // Usage limits (set by admin)
  minutesUsed: number
  minutesLimit: number
  voiceIdsUsed: number
  voiceIdsLimit: number
  storageUsed?: number // bytes
  storageLimit?: number // bytes

  // Admin fields
  isAdmin?: boolean
  isActive?: boolean
  notes?: string // Admin notes about client
  company?: string

  // API usage tracking
  openaiTokensUsed?: number
  replicateMinutesUsed?: number

  createdAt: string
  updatedAt?: string
}

// Admin emails list
export const ADMIN_EMAILS = ['admin@gmail.com']

export function isAdminUser(email?: string): boolean {
  return email ? ADMIN_EMAILS.includes(email.toLowerCase()) : false
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean

  // Actions
  setUser: (user: User | null) => void
  setLoading: (loading: boolean) => void
  updateUser: (updates: Partial<User>) => void
  logout: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
          isLoading: false,
        }),

      setLoading: (isLoading) => set({ isLoading }),

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      logout: () =>
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        }),
    }),
    {
      name: 'nexscribe-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Set loading to false after rehydration
        if (state) {
          state.isLoading = false
        }
      },
    }
  )
)

// Recordings store
export interface Recording {
  id: string
  title: string
  fileName: string
  duration: number // seconds
  fileSize: number // bytes
  status: 'uploading' | 'processing' | 'transcribing' | 'analyzing' | 'completed' | 'error'
  progress: number // 0-100
  createdAt: string
  updatedAt: string

  // Storage
  audioUrl?: string
  error?: string

  // Diarization settings
  numSpeakers?: number | null // null = auto-detect, number = exact count

  // Results
  transcript?: TranscriptSegment[]
  speakers?: Speaker[]
  summary?: string
  actionItems?: ActionItem[]
  analysis?: Analysis
}

export interface TranscriptSegment {
  id: string
  speakerId: string
  text: string
  startTime: number
  endTime: number
  confidence: number
}

export interface Speaker {
  id: string
  label: string // "Speaker 1", "Speaker 2", etc.
  name?: string // User-assigned name
  voiceId?: string // Linked voice profile
  color: string
  speakingTime: number // seconds
}

export interface ActionItem {
  id: string
  text: string
  owner?: string
  dueDate?: string
  priority: 'low' | 'medium' | 'high'
  completed: boolean
}

export interface Analysis {
  summary: string
  keyPoints: string[]
  decisions: string[]
  questions: string[]
  sentiment: 'positive' | 'neutral' | 'negative'
  topics: string[]
}

interface RecordingsState {
  recordings: Recording[]
  currentRecording: Recording | null
  isUploading: boolean
  uploadProgress: number

  // Actions
  addRecording: (recording: Recording) => void
  updateRecording: (id: string, updates: Partial<Recording>) => void
  removeRecording: (id: string) => void
  setCurrentRecording: (recording: Recording | null) => void
  setRecordings: (recordings: Recording[]) => void
  setUploading: (isUploading: boolean, progress?: number) => void
}

export const useRecordingsStore = create<RecordingsState>((set) => ({
  recordings: [],
  currentRecording: null,
  isUploading: false,
  uploadProgress: 0,

  addRecording: (recording) =>
    set((state) => ({
      recordings: [recording, ...state.recordings],
    })),

  updateRecording: (id, updates) =>
    set((state) => ({
      recordings: state.recordings.map((r) => (r.id === id ? { ...r, ...updates } : r)),
      currentRecording:
        state.currentRecording?.id === id
          ? { ...state.currentRecording, ...updates }
          : state.currentRecording,
    })),

  removeRecording: (id) =>
    set((state) => ({
      recordings: state.recordings.filter((r) => r.id !== id),
      currentRecording: state.currentRecording?.id === id ? null : state.currentRecording,
    })),

  setCurrentRecording: (recording) => set({ currentRecording: recording }),

  setRecordings: (recordings) => set({ recordings }),

  setUploading: (isUploading, progress = 0) =>
    set({
      isUploading,
      uploadProgress: progress,
    }),
}))

// Voice Library store
export interface VoiceProfile {
  id: string
  name: string
  email?: string
  company?: string
  role?: string
  avatar?: string
  sampleCount: number
  lastSeen: string
  createdAt: string
  isVerified: boolean
}

interface VoiceLibraryState {
  profiles: VoiceProfile[]
  isTraining: boolean

  // Actions
  addProfile: (profile: VoiceProfile) => void
  updateProfile: (id: string, updates: Partial<VoiceProfile>) => void
  removeProfile: (id: string) => void
  setProfiles: (profiles: VoiceProfile[]) => void
  setTraining: (isTraining: boolean) => void
}

export const useVoiceLibraryStore = create<VoiceLibraryState>((set) => ({
  profiles: [],
  isTraining: false,

  addProfile: (profile) =>
    set((state) => ({
      profiles: [profile, ...state.profiles],
    })),

  updateProfile: (id, updates) =>
    set((state) => ({
      profiles: state.profiles.map((p) => (p.id === id ? { ...p, ...updates } : p)),
    })),

  removeProfile: (id) =>
    set((state) => ({
      profiles: state.profiles.filter((p) => p.id !== id),
    })),

  setProfiles: (profiles) => set({ profiles }),

  setTraining: (isTraining) => set({ isTraining }),
}))

// Processing Queue Store - Global state for files being processed
export interface ProcessingFile {
  id: string // Local ID for tracking
  recordingId: string // Firestore document ID
  fileName: string
  fileSize: number
  status: 'uploading' | 'processing' | 'transcribing' | 'analyzing' | 'completed' | 'error'
  progress: number // Upload progress 0-100
  error?: string
  startedAt: string
}

interface ProcessingState {
  processingFiles: ProcessingFile[]

  // Actions
  addProcessingFile: (file: ProcessingFile) => void
  updateProcessingFile: (id: string, updates: Partial<ProcessingFile>) => void
  removeProcessingFile: (id: string) => void
  clearCompleted: () => void
  getProcessingCount: () => number
}

export const useProcessingStore = create<ProcessingState>((set, get) => ({
  processingFiles: [],

  addProcessingFile: (file) =>
    set((state) => ({
      processingFiles: [file, ...state.processingFiles],
    })),

  updateProcessingFile: (id, updates) =>
    set((state) => ({
      processingFiles: state.processingFiles.map((f) =>
        f.id === id ? { ...f, ...updates } : f
      ),
    })),

  removeProcessingFile: (id) =>
    set((state) => ({
      processingFiles: state.processingFiles.filter((f) => f.id !== id),
    })),

  clearCompleted: () =>
    set((state) => ({
      processingFiles: state.processingFiles.filter(
        (f) => f.status !== 'completed' && f.status !== 'error'
      ),
    })),

  getProcessingCount: () => {
    const state = get()
    return state.processingFiles.filter(
      (f) => f.status !== 'completed' && f.status !== 'error'
    ).length
  },
}))
